import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import DashboardLayout from "../layout/DashboardLayout";

import Banner1 from "../assets/Banner_1.jpeg";
import Banner2 from "../assets/Banner_2.jpeg";

const PASSES = [
  {
    key: "hourly",
    title: "Hourly Pass",
    desc: "Perfect for quick sessions and meetings",
    durationLabel: "Duration: Hourly",
    price: 300
  },
  {
    key: "day",
    title: "Day Pass",
    desc: "Ideal for regular workspace users",
    durationLabel: "Duration: Full Day",
    price: 1800
  },
  {
    key: "custom",
    title: "Custom Pass",
    desc: "Best value for dedicated professionals",
    durationLabel: "Duration: Custom",
    price: 6500
  }
];

function money(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString("en-IN")}`;
}

function toDateInputValue(d = new Date()) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function BookSeat() {
  const [selectedPass, setSelectedPass] = useState("hourly");

  const [seatDate, setSeatDate] = useState(toDateInputValue(new Date()));
  const [startTime, setStartTime] = useState("11:00");
  const [endTime, setEndTime] = useState("12:00");

  const [discount] = useState(0);

  const pass = useMemo(
    () => PASSES.find((p) => p.key === selectedPass) || PASSES[0],
    [selectedPass]
  );

  const subtotal = pass.price;
  const total = Math.max(0, subtotal - Number(discount || 0));

  const banners = useMemo(
    () => [
      { id: "b1", image: Banner1 },
      { id: "b2", image: Banner2 }
    ],
    []
  );

  const showTimeInputs = selectedPass === "hourly";

  const handleProceed = () => {
    if (!seatDate) return alert("Please select a date.");
    if (showTimeInputs && (!startTime || !endTime)) return alert("Please select start and end time.");

    const payload = {
      passType: selectedPass,
      date: seatDate,
      startTime: showTimeInputs ? startTime : null,
      endTime: showTimeInputs ? endTime : null,
      amount: subtotal,
      discount: Number(discount || 0),
      total
    };

    console.log("Book seat payload:", payload);
    alert("Proceed to Payment (hook payment next).");
  };

  return (
    <DashboardLayout title="Dashboard">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" },
          gap: 3
        }}
      >
        {/* LEFT */}
        <Box sx={{ minWidth: 0 }}>
          {/* Top strip (same pattern as Home welcome card) */}
          <Card sx={{ bgcolor: "action.selected", borderColor: "divider" }}>
            <CardContent sx={{ py: 2.25 }}>
              <Typography sx={{ color: "text.primary", fontWeight: 900 }}>
                Reserve your workspace
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: 13, color: "text.secondary" }}>
                Choose a pass and customise your NeherBaari experience.
              </Typography>
            </CardContent>
          </Card>

          {/* Select Your Pass (use same section header class) */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Select Your Pass</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 2
              }}
            >
              {PASSES.map((p) => {
                const active = p.key === selectedPass;

                return (
                  <Card
                    key={p.key}
                    onClick={() => setSelectedPass(p.key)}
                    sx={{
                      cursor: "pointer",
                      overflow: "hidden",
                      "&:hover": { backgroundColor: "action.hover" },
                      borderColor: active ? "primary.main" : "divider"
                    }}
                  >
                    <CardContent sx={{ pt: 2.1, pb: 2.2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 2,
                            bgcolor: "action.hover",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "primary.main",
                            fontWeight: 900,
                            fontSize: 13
                          }}
                        >
                          ✳
                        </Box>

                        <IconButton size="small" disableRipple>
                          {active ? (
                            <CheckCircleRoundedIcon sx={{ color: "primary.main" }} />
                          ) : (
                            <RadioButtonUncheckedRoundedIcon sx={{ color: "text.disabled" }} />
                          )}
                        </IconButton>
                      </Box>

                      <Typography sx={{ mt: 1.2, fontWeight: 900, fontSize: 13.5, color: "text.primary" }}>
                        {p.title}
                      </Typography>

                      <Typography sx={{ mt: 0.6, fontSize: 12.5, color: "text.secondary", lineHeight: 1.6 }}>
                        {p.desc}
                      </Typography>

                      <Typography sx={{ mt: 1.0, fontSize: 12.5, color: "text.secondary" }}>
                        {p.durationLabel}
                      </Typography>

                      <Typography sx={{ mt: 1.1, fontWeight: 900, color: "primary.main" }}>
                        {money(p.price)}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>

          {/* Seat & Schedule */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Seat & Schedule</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Card>
              <CardContent sx={{ py: 2.2 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: showTimeInputs ? "1fr 1fr 1fr" : "1fr" },
                    gap: 2
                  }}
                >
                  <FormControl>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                      Select Date
                    </Typography>
                    <TextField
                      type="date"
                      value={seatDate}
                      onChange={(e) => setSeatDate(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </FormControl>

                  {showTimeInputs && (
                    <>
                      <FormControl>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                          Start Time
                        </Typography>
                        <TextField
                          type="time"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccessTimeOutlinedIcon fontSize="small" />
                              </InputAdornment>
                            )
                          }}
                        />
                      </FormControl>

                      <FormControl>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", mb: 0.7 }}>
                          End Time
                        </Typography>
                        <TextField
                          type="time"
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccessTimeOutlinedIcon fontSize="small" />
                              </InputAdornment>
                            )
                          }}
                        />
                      </FormControl>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Booking Summary (use action.selected like Home chips/cards) */}
          <Box sx={{ mt: 3 }}>
            <Card sx={{ bgcolor: "action.selected", borderColor: "divider" }}>
              <CardContent sx={{ py: 2.2 }}>
                <Typography sx={{ fontWeight: 900, color: "text.primary", fontSize: 13.5 }}>
                  Booking Summary
                </Typography>

                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Typography sx={{ fontSize: 12.8, color: "text.secondary", fontWeight: 700 }}>
                      {pass.title} (1)
                    </Typography>
                    <Typography sx={{ fontSize: 12.8, fontWeight: 900, color: "text.primary" }}>
                      {money(subtotal)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 1.1 }}>
                    <Typography sx={{ fontSize: 12.8, color: "text.secondary", fontWeight: 700 }}>
                      Discount
                    </Typography>
                    <Typography sx={{ fontSize: 12.8, fontWeight: 900, color: "text.primary" }}>
                      {money(discount)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1.6 }} />

                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 900, color: "text.primary" }}>
                      Total Amount
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 900, color: "primary.main" }}>
                      {money(total)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Proceed button (no manual colors, theme variant only) */}
          <Box sx={{ mt: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Button variant="contained" onClick={handleProceed} sx={{ px: 5, py: 1.2 }}>
              Proceed to Payment
            </Button>

            <Typography sx={{ mt: 1.1, fontSize: 12, color: "text.secondary" }}>
              You can review details before final confirmation.
            </Typography>
          </Box>
        </Box>

        {/* RIGHT banners (reuse Home style) */}
        <Box sx={{ display: { xs: "none", lg: "flex" }, flexDirection: "column", gap: 2 }}>
          {banners.map((b) => (
            <Card key={b.id} sx={{ overflow: "hidden", bgcolor: "background.paper" }}>
              <Box sx={{ p: 1.5, bgcolor: "action.hover" }}>
                <Box
                  sx={{
                    height: 320,
                    borderRadius: 0,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    backgroundImage: `url(${b.image})`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "contain"
                  }}
                />
              </Box>

              <Box sx={{ p: 1.5, pt: 1.2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {}}
                  sx={{
                    borderColor: "primary.main",
                    color: "primary.main",
                    fontWeight: 800,
                    borderRadius: 14,
                    py: 1
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Card>
          ))}
        </Box>
      </Box>
    </DashboardLayout>
  );
}
